import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  getAllTeachers,
  getAllAssignments,
  createAssignment,
  getAssignmentsByTeacherId,
  toggleTeacherStatus,
  deleteAssignment,
  updateTeacherPhoneNumber,
  updateBuraxilisAssignmentQuestions,
  getStudentPrefixesForAssignment,
  updateAssignmentPrefixFilter
} from '../../services/adminService';
import { getSelectedExam } from '../../services/examService';
import { Teacher, YoklaAssignment, StudentIdPrefixFilter } from '../../types';
import { 
  ALL_FENNLER, 
  SINIFLAR, 
  QRUPLAR
} from '../../constants/fennConfig';
import BuraxilisAssignmentForm from './BuraxilisAssignmentForm';
import BuraxilisQuestionCheckboxGroup from './BuraxilisQuestionCheckboxGroup';
import PrefixFilterPanel from './PrefixFilterPanel';
import type { PrefixInfo } from '../../utils/studentIdPrefixUtils';

const Container = styled.div`
  min-height: 100vh;
  background: #1e3a8a;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  ${props => {
    switch (props.variant) {
      case 'danger':
        return `
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3); }
        `;
      case 'secondary':
        return `
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          color: #475569;
          &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(100, 116, 139, 0.2); }
        `;
      default:
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3); }
        `;
    }
  }}

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 14px;
    flex: 1;
  }
`;

const PoolButton = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  color: white;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(6, 182, 212, 0.3);
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 14px;
    flex: 1;
  }
`;

const SearchAndFilterSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  margin-bottom: 16px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 12px 16px;
  }
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 10px 14px;
  }
`;

const TeachersList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const TeacherCard = styled.div<{ isInactive?: boolean }>`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  opacity: ${props => props.isInactive ? 0.6 : 1};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${props => props.isInactive 
      ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' 
      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    };
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const TeacherHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const TeacherInfo = styled.div`
  flex: 1;
`;

const TeacherName = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const TeacherFenn = styled.div`
  display: inline-block;
  padding: 6px 12px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const TeacherID = styled.div`
  font-size: 13px;
  color: #64748b;
`;

const StatusBadge = styled.div<{ isActive: boolean }>`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.isActive 
    ? 'linear-gradient(135deg, #10b981, #059669)' 
    : 'linear-gradient(135deg, #ef4444, #dc2626)'
  };
  color: white;
  box-shadow: 0 2px 8px ${props => props.isActive 
    ? 'rgba(16, 185, 129, 0.3)' 
    : 'rgba(239, 68, 68, 0.3)'
  };
`;

const AssignmentSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 2px solid #f1f5f9;
`;

const AssignmentTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  margin: 0 0 12px 0;
`;

const AssignmentForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AssignmentRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SmallSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const SmallButton = styled.button<{ variant?: 'success' | 'danger' }>`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 6px;
  
  ${props => props.variant === 'danger' ? `
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
  ` : `
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
  `}

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const TeacherAssignments = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AssignmentTag = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 8px;
  font-size: 13px;
  border: 1px solid #e2e8f0;
  gap: 8px;
`;

const AssignmentText = styled.span`
  color: #475569;
  font-weight: 500;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: white;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: white;
  font-size: 18px;
`;

const DeleteButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const EditButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const AssignmentTagButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const ConfirmDialog = styled.div<{ show: boolean }>`
  display: ${props => props.show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  z-index: 2000;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ConfirmDialogContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 450px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ConfirmDialogTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 16px 0;
`;

const ConfirmDialogText = styled.p`
  font-size: 15px;
  color: #64748b;
  margin: 0 0 24px 0;
  line-height: 1.6;
`;

const ConfirmDialogButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ConfirmButton = styled.button<{ variant?: 'danger' | 'cancel' }>`
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  
  ${props => props.variant === 'danger' ? `
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
    }
  ` : `
    background: #f1f5f9;
    color: #475569;
    &:hover {
      background: #e2e8f0;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditModal = styled.div<{ show: boolean }>`
  display: ${props => props.show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  z-index: 2000;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const EditModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 520px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-height: 90vh;
  overflow-y: auto;
`;

const TeacherManagement: React.FC = () => {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [filteredTeachers, setFilteredTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [fennFilter, setFennFilter] = useState('');
  const [assignmentSinifFilter, setAssignmentSinifFilter] = useState('');
  const [assignmentQrupFilter, setAssignmentQrupFilter] = useState('');
  const [assignmentFennFilter, setAssignmentFennFilter] = useState('');
  const [teacherAssignments, setTeacherAssignments] = useState<{ [teacherId: string]: YoklaAssignment[] }>({});
  const [isBuraxilis, setIsBuraxilis] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ assignmentId: string; assignmentInfo: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editAssignment, setEditAssignment] = useState<YoklaAssignment | null>(null);
  const [questionsToKeep, setQuestionsToKeep] = useState<string[]>([]);
  const [isEditingAssignment, setIsEditingAssignment] = useState(false);
  const [editPrefixFilter, setEditPrefixFilter] = useState<StudentIdPrefixFilter | undefined>(undefined);
  const [editAvailablePrefixes, setEditAvailablePrefixes] = useState<PrefixInfo[]>([]);
  const [editPrefixLoading, setEditPrefixLoading] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState<{ [teacherId: string]: string }>({});
  const [isUpdatingPhone, setIsUpdatingPhone] = useState<{ [teacherId: string]: boolean }>({});
  const [submittingTeacherId, setSubmittingTeacherId] = useState<string | null>(null);

  // Her öğretmen için atama form state'leri
  const [assignmentForms, setAssignmentForms] = useState<{
    [teacherId: string]: {
      sinif: string;
      qrup: string;
      subGroup: string;
      selectedFenn: string; // Çift branşlı öğretmenler için seçilen fenn
      buildingName?: string; // Buraxılış için bina seçimi
      examType?: 'Blok' | 'buraxilis'; // Sınav tipi
    }
  }>({});

  useEffect(() => {
    loadTeachers();
  }, []);

  useEffect(() => {
    filterTeachers();
  }, [searchTerm, fennFilter, teachers]);

  const loadTeachers = async () => {
    try {
      setLoading(true);
      
      // Seçili imtahanı al
      const selectedExam = getSelectedExam();
      const collectionFilter = selectedExam?.collection;
      const isBuraxilisExam = selectedExam?.type === 'buraxilis';
      
      setIsBuraxilis(isBuraxilisExam);
      
      console.log('📚 TeacherManagement: Seçili imtahan:', selectedExam);
      console.log('🔍 TeacherManagement: Collection filter:', collectionFilter);
      console.log('🎓 TeacherManagement: Buraxılış modu:', isBuraxilisExam);
      
      // OPTIMIZASYON: Tüm verileri PARALEL çek
      const [allTeachers, allAssignments] = await Promise.all([
        getAllTeachers(),
        getAllAssignments(collectionFilter)
      ]);
      
      console.log('✅ TeacherManagement: Yüklenen atamalar:', allAssignments.length);
      
      // Buraxılış üçün yalnız 6 fənn müəllimləri (baza adlar: İngilis, Rus, Alman, Fransız + Azərbaycan dili, Riyaziyyat)
      const BURAXILIS_FENNS_BASE = ['Azərbaycan dili', 'Riyaziyyat', 'İngilis', 'Rus', 'Alman', 'Fransız'];
      
      const filteredTeachers = isBuraxilisExam 
        ? allTeachers.filter(t => {
            const teacherFenns = t.fenn.split(',').map(f => normalizeFenn(f));
            return teacherFenns.some(f => BURAXILIS_FENNS_BASE.includes(toBaseFenn(f)));
          })
        : allTeachers;
      
      console.log(`✅ TeacherManagement: ${isBuraxilisExam ? 'Buraxılış' : 'Blok'} müəllimləri:`, filteredTeachers.length);
      
      setTeachers(filteredTeachers);
      
      // Atamaları öğretmenlere göre grupla (frontend'de)
      const assignmentsMap: { [teacherId: string]: YoklaAssignment[] } = {};
      filteredTeachers.forEach(teacher => {
        assignmentsMap[teacher.id] = allAssignments.filter((a: YoklaAssignment) => a.hocaId === teacher.id);
      });
      setTeacherAssignments(assignmentsMap);
      
      // Form state'lerini başlat
      const initialForms: typeof assignmentForms = {};
      const initialPhoneNumbers: { [teacherId: string]: string } = {};
      filteredTeachers.forEach(teacher => {
        // Öğretmenin fenn'ini parse et (virgülle ayrılmış olabilir) ve normalize et
        const fennList = teacher.fenn.split(',').map(f => normalizeFenn(f));
        initialForms[teacher.id] = {
          sinif: '',
          qrup: isBuraxilisExam ? '' : '', // Buraxılışda qrup yoxdur
          subGroup: '',
          selectedFenn: fennList[0], // Varsayılan olarak ilk fenn'i seç
          buildingName: 'Hamısı',
          examType: isBuraxilisExam ? 'buraxilis' : 'Blok'
        };
        // Telefon numarasından +994'ü kaldır, sadece 9 rakamı göster
        let displayPhone = teacher.phoneNumber || '';
        if (displayPhone.startsWith('+994')) {
          displayPhone = displayPhone.substring(4);
        } else if (displayPhone.startsWith('994')) {
          displayPhone = displayPhone.substring(3);
        }
        initialPhoneNumbers[teacher.id] = displayPhone;
      });
      setAssignmentForms(initialForms);
      setPhoneNumbers(initialPhoneNumbers);
    } catch (error) {
      console.error('Öğretmenler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper: Normalize fenn for display (keep as-is; display may add "dili" elsewhere)
  const normalizeFenn = (fenn: string): string => fenn.trim();

  // Xarici dil: "İngilis dili" / "İngilis" -> "İngilis" (baza ad) - müqayisə üçün
  const toBaseFenn = (fenn: string): string => {
    const t = normalizeFenn(fenn);
    if (t === 'İngilis dili' || t === 'İngilis') return 'İngilis';
    if (t === 'Rus dili' || t === 'Rus') return 'Rus';
    if (t === 'Alman dili' || t === 'Alman') return 'Alman';
    if (t === 'Fransız dili' || t === 'Fransız') return 'Fransız';
    return t;
  };

  // Təyinat yadda saxlanarkən fənn həmişə konfiq formatında olsun (İngilis dili, Rus dili, ...)
  const toCanonicalFenn = (fenn: string): string => {
    const base = toBaseFenn(fenn);
    if (base === 'İngilis') return 'İngilis dili';
    if (base === 'Rus') return 'Rus dili';
    if (base === 'Alman') return 'Alman dili';
    if (base === 'Fransız') return 'Fransız dili';
    return fenn.trim();
  };

  // Azerbaijani-aware normalization for search: handles dotless ı (U+0131) which JS toLowerCase misses
  const normalizeAz = (str: string): string => {
    return str
      .replace(/İ/g, 'i')  // dotted capital I → i
      .replace(/ı/g, 'i')  // dotless ı → i (JS toLowerCase does NOT convert this)
      .toLowerCase()
      .trim();
  };

  const filterTeachers = () => {
    let filtered = teachers;

    // Fenn filtreleme (multi-fenn + xarici dil: "İngilis dili" / "İngilis" eşleşmeli)
    if (fennFilter) {
      filtered = filtered.filter(t => {
        const teacherFenns = t.fenn.split(',').map(f => normalizeFenn(f));
        return teacherFenns.some(f => toBaseFenn(f) === toBaseFenn(fennFilter));
      });
    }

    // Arama (isim ve ID)
    if (searchTerm) {
      const term = normalizeAz(searchTerm);
      filtered = filtered.filter(t =>
        normalizeAz(t.name).includes(term) ||
        normalizeAz(t.id).includes(term)
      );
    }

    setFilteredTeachers(filtered);
  };

  // Atamaları filtrele
  const filterAssignments = (assignments: YoklaAssignment[]): YoklaAssignment[] => {
    let filtered = assignments;

    if (assignmentSinifFilter) {
      filtered = filtered.filter(a => a.sinif === assignmentSinifFilter);
    }

    if (assignmentQrupFilter) {
      filtered = filtered.filter(a => a.qrup === assignmentQrupFilter);
    }

    if (assignmentFennFilter) {
      filtered = filtered.filter(a => toBaseFenn(a.fenn) === toBaseFenn(assignmentFennFilter));
    }

    return filtered;
  };

  const handleAssignmentFormChange = (teacherId: string, field: string, value: string) => {
    setAssignmentForms(prev => ({
      ...prev,
      [teacherId]: {
        ...prev[teacherId],
        [field]: value
      }
    }));
  };

  const handleCreateAssignment = async (teacher: Teacher) => {
    const form = assignmentForms[teacher.id];
    const selectedExam = getSelectedExam(); // Import from examService
    
    if (!selectedExam) {
      alert('Lütfən əvvəlcə imtahan seçin');
      return;
    }

    if (!form.sinif) {
      alert('Lütfən sinif seçin');
      return;
    }

    if (form.examType === 'Blok' && !form.qrup) {
      alert('Lütfən qrup seçin');
      return;
    }

    if (!form.selectedFenn) {
      alert('Lütfən fənn seçin');
      return;
    }

    try {
      const examType = form.examType || 'Blok';
      let questionNumbers: string[] | undefined;
      
      // For buraxılış, get question numbers based on grade and fenn
      if (examType === 'buraxilis') {
        const { getBuraxilisQuestionsByGrade } = await import('../../services/examService');
        questionNumbers = getBuraxilisQuestionsByGrade(Number(form.sinif), form.selectedFenn, selectedExam);
        
        if (!questionNumbers || questionNumbers.length === 0) {
          alert(`${form.sinif}-ci sinif üçün ${form.selectedFenn} fənnində sual tapılmadı`);
          return;
        }
      }

      const canonicalFenn = examType === 'buraxilis' ? toCanonicalFenn(form.selectedFenn) : form.selectedFenn;
      const result = await createAssignment(
        teacher.id,
        teacher.name,
        canonicalFenn,
        form.sinif,
        form.qrup || '-', // Buraxılış'te qrup yok
        selectedExam.collection,
        examType,
        form.buildingName,
        questionNumbers
      );

      if (result.success) {
        // Display the result message which includes question numbers for Blok
        alert(result.message);
        // Yeniden yükle
        await loadTeachers();
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error('Atama oluşturulurken hata:', error);
      alert('Təyinat yaradılarkən xəta baş verdi');
    }
  };

  const handleToggleTeacherStatus = async (teacherId: string, currentStatus: boolean) => {
    try {
      await toggleTeacherStatus(teacherId, !currentStatus);
      alert(`Öğretmen ${!currentStatus ? 'aktif' : 'pasif'} edildi`);
      await loadTeachers();
    } catch (error) {
      console.error('Öğretmen statusu değiştirilirken hata:', error);
      alert('Bir hata oluştu');
    }
  };

  const handleDeleteAssignment = async () => {
    if (!deleteConfirm) return;

    try {
      setIsDeleting(true);
      await deleteAssignment(deleteConfirm.assignmentId);
      alert('✅ Atama uğurla silindi');
      setDeleteConfirm(null);
      await loadTeachers();
    } catch (error) {
      console.error('Atama silinirken hata:', error);
      alert('Atama silinərkən xəta baş verdi');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <LoadingSpinner />
          <div>Öğretmenler yükleniyor...</div>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Öğretmen Yönetimi ve Atama</Title>
        <ButtonGroup>
          {isBuraxilis && (
            <PoolButton onClick={() => navigate('/admin/assignments?openPoolModal=true')}>
              🏊 Hovuz Ataması Yarat
            </PoolButton>
          )}
          <Button onClick={() => navigate('/admin/dashboard')}>
            ← Geri Dön
          </Button>
        </ButtonGroup>
      </Header>

      <SearchAndFilterSection>
        <SearchInput
          type="text"
          placeholder="🔍 Öğretmen adı veya ID ile ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div style={{ marginTop: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>
            Öğretmen Filtreləri:
          </div>
          <FilterGrid>
            <Select value={fennFilter} onChange={(e) => setFennFilter(e.target.value)}>
              <option value="">Tüm Fennler</option>
              {(isBuraxilis
                ? ['Azərbaycan dili', 'Riyaziyyat', 'İngilis', 'Rus', 'Alman', 'Fransız']
                : ALL_FENNLER
              ).map(fenn => (
                <option key={fenn} value={fenn}>{fenn}</option>
              ))}
            </Select>
          </FilterGrid>
        </div>
        <div style={{ marginTop: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>
            Təyinat Filtreləri:
          </div>
          <FilterGrid>
            <Select value={assignmentFennFilter} onChange={(e) => setAssignmentFennFilter(e.target.value)}>
              <option value="">Tüm Fennər</option>
              {(isBuraxilis
                ? ['Azərbaycan dili', 'Riyaziyyat', 'İngilis', 'Rus', 'Alman', 'Fransız']
                : ALL_FENNLER
              ).map(fenn => (
                <option key={fenn} value={fenn}>{fenn}</option>
              ))}
            </Select>
            <Select value={assignmentSinifFilter} onChange={(e) => setAssignmentSinifFilter(e.target.value)}>
              <option value="">Tüm Siniflər</option>
              {SINIFLAR.map(sinif => (
                <option key={sinif} value={sinif}>{sinif}. Sinif</option>
              ))}
            </Select>
            <Select value={assignmentQrupFilter} onChange={(e) => setAssignmentQrupFilter(e.target.value)}>
              <option value="">Tüm Qruplar</option>
              {QRUPLAR.map(qrup => (
                <option key={qrup} value={qrup}>Qrup {qrup}</option>
              ))}
            </Select>
          </FilterGrid>
        </div>
        {(assignmentFennFilter || assignmentSinifFilter || assignmentQrupFilter) && (
          <div style={{ marginTop: '12px', textAlign: 'center' }}>
            <button
              onClick={() => {
                setAssignmentFennFilter('');
                setAssignmentSinifFilter('');
                setAssignmentQrupFilter('');
              }}
              style={{
                padding: '8px 16px',
                background: '#f1f5f9',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: '#64748b'
              }}
            >
              🔄 Təyinat Filtrlərini Təmizlə
            </button>
          </div>
        )}
      </SearchAndFilterSection>

      {filteredTeachers.length === 0 ? (
        <EmptyState>
          Hiç öğretmen bulunamadı
        </EmptyState>
      ) : (
        <TeachersList>
          {filteredTeachers.map(teacher => (
            <TeacherCard key={teacher.id} isInactive={teacher.isActive === false}>
              <TeacherHeader>
                <TeacherInfo>
                  <TeacherName>{teacher.name}</TeacherName>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    {teacher.fenn.split(',').map((fenn, idx) => (
                      <TeacherFenn key={idx}>{normalizeFenn(fenn)}</TeacherFenn>
                    ))}
                  </div>
                  <TeacherID>ID: {teacher.id}</TeacherID>
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                      Telefon Nömrəsi:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0', flex: 1, minWidth: 0 }}>
                        <div style={{
                          padding: '8px 12px',
                          background: '#f8fafc',
                          border: '2px solid #e2e8f0',
                          borderRight: 'none',
                          borderTopLeftRadius: '8px',
                          borderBottomLeftRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#475569',
                          whiteSpace: 'nowrap',
                          flexShrink: 0
                        }}>
                          +994
                        </div>
                        <input
                          type="tel"
                          value={phoneNumbers[teacher.id] || ''}
                          onChange={(e) => {
                            let value = e.target.value;
                            // Sadece rakamları al
                            value = value.replace(/[^0-9]/g, '');
                            
                            // Öndeki 0'ı sil
                            if (value.startsWith('0')) {
                              value = value.substring(1);
                            }
                            
                            // +994'ü kaldır (eğer varsa)
                            if (value.startsWith('994')) {
                              value = value.substring(3);
                            }
                            
                            // Maksimum 9 rakam (994'den sonra)
                            if (value.length > 9) {
                              value = value.substring(0, 9);
                            }
                            
                            setPhoneNumbers(prev => ({ ...prev, [teacher.id]: value }));
                          }}
                          placeholder="501234567"
                          style={{
                            flex: 1,
                            minWidth: 0,
                            padding: '8px 12px',
                            border: '2px solid #e2e8f0',
                            borderLeft: 'none',
                            borderTopRightRadius: '8px',
                            borderBottomRightRadius: '8px',
                            fontSize: '14px',
                            transition: 'all 0.2s ease'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#3b82f6';
                            const prevSibling = e.target.parentElement?.previousElementSibling as HTMLElement;
                            if (prevSibling) {
                              prevSibling.style.borderColor = '#3b82f6';
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e2e8f0';
                            const prevSibling = e.target.parentElement?.previousElementSibling as HTMLElement;
                            if (prevSibling) {
                              prevSibling.style.borderColor = '#e2e8f0';
                            }
                          }}
                        />
                      </div>
                      <SmallButton
                        onClick={async () => {
                          let phoneNumber = phoneNumbers[teacher.id] || '';
                          
                          if (!phoneNumber.trim()) {
                            alert('Zəhmət olmasa telefon nömrəsi daxil edin');
                            return;
                          }
                          
                          // Sadece rakamları al
                          phoneNumber = phoneNumber.replace(/[^0-9]/g, '');
                          
                          // Öndeki 0'ı sil
                          if (phoneNumber.startsWith('0')) {
                            phoneNumber = phoneNumber.substring(1);
                          }
                          
                          // +994'ü kaldır (eğer varsa)
                          if (phoneNumber.startsWith('994')) {
                            phoneNumber = phoneNumber.substring(3);
                          }
                          
                          // +994'den sonra tam 9 rakam olmalı
                          if (phoneNumber.length !== 9) {
                            alert('Telefon nömrəsi +994\'dən sonra tam 9 rəqəm olmalıdır (məs: 501234567)');
                            return;
                          }
                          
                          // +994 ekle
                          const formattedPhone = `+994${phoneNumber}`;
                          
                          try {
                            setIsUpdatingPhone(prev => ({ ...prev, [teacher.id]: true }));
                            await updateTeacherPhoneNumber(teacher.id, formattedPhone);
                            alert(`✅ Telefon nömrəsi uğurla yeniləndi\n\n📁 Firebase: users/${teacher.id}/phoneNumber\n💾 Dəyər: ${formattedPhone}`);
                            await loadTeachers();
                          } catch (error) {
                            console.error('Telefon nömrəsi yenilənərkən xəta:', error);
                            alert('Telefon nömrəsi yenilənərkən xəta baş verdi');
                          } finally {
                            setIsUpdatingPhone(prev => ({ ...prev, [teacher.id]: false }));
                          }
                        }}
                        disabled={isUpdatingPhone[teacher.id]}
                        style={{ width: '100%', justifyContent: 'center' }}
                      >
                        {isUpdatingPhone[teacher.id] ? (
                          <span style={{ display: 'inline-block', width: '12px', height: '12px', border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                            <polyline points="17 21 17 13 7 13 7 21"></polyline>
                            <polyline points="7 3 7 8 15 8"></polyline>
                          </svg>
                        )}
                        <span>{isUpdatingPhone[teacher.id] ? 'Yadda saxlanır...' : 'Yadda saxla'}</span>
                      </SmallButton>
                    </div>
                  </div>
                </TeacherInfo>
                <StatusBadge isActive={teacher.isActive !== false}>
                  {teacher.isActive !== false ? 'Aktif' : 'Pasif'}
                </StatusBadge>
              </TeacherHeader>

              {/* Mevcut Atamalar */}
              {teacherAssignments[teacher.id] && teacherAssignments[teacher.id].length > 0 && (() => {
                const filteredAssignments = filterAssignments(teacherAssignments[teacher.id]);
                return filteredAssignments.length > 0 && (
                  <TeacherAssignments>
                    <AssignmentTitle>Mevcut Atamalar ({filteredAssignments.length}):</AssignmentTitle>
                    {filteredAssignments.map(assignment => {
                      const assignmentInfo = `${assignment.fenn} • Sınıf ${assignment.sinif} • ${isBuraxilis ? `Variant ${assignment.variant || assignment.qrup}` : `Qrup ${assignment.qrup}`}`;
                      const isEmptyAssignment = (assignment.totalCount ?? 0) === 0;
                      return (
                        <AssignmentTag key={assignment.id} style={isEmptyAssignment ? {
                          background: 'linear-gradient(135deg, #fef9c3, #fef08a)',
                          border: '1px solid #f59e0b',
                          opacity: 0.9
                        } : {}}>
                          <AssignmentText style={{ flex: 1 }}>
                            {isEmptyAssignment && (
                              <span style={{
                                display: 'inline-block',
                                background: '#f59e0b',
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 700,
                                padding: '1px 6px',
                                borderRadius: '4px',
                                marginRight: '6px',
                                verticalAlign: 'middle'
                              }}>⚠ Boş atama</span>
                            )}
                            {assignment.fenn} • Sınıf {assignment.sinif} • {isBuraxilis ? `Variant ${assignment.variant || assignment.qrup}` : `Qrup ${assignment.qrup}`}
                            {assignment.questionNumbers && isBuraxilis && (
                              <span> • Suallar: {assignment.questionNumbers.map((q, idx) => (
                                <span key={q} style={{ 
                                  display: 'inline-block',
                                  padding: '2px 6px',
                                  margin: '0 2px',
                                  background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                                  borderRadius: '4px',
                                  fontSize: '0.85em',
                                  fontWeight: '600',
                                  color: '#1e40af'
                                }}>
                                  {q}
                                </span>
                              ))}</span>
                            )}
                            {assignment.questionNumber && !assignment.questionNumbers && ` • ${assignment.questionNumber}`}
                            {assignment.subGroup && !isBuraxilis && ` • ${assignment.subGroup}`}
                            {' • '}İləriləmə {assignment.completedCount}/{assignment.totalCount}
                            {' • '}{assignment.status === 'completed' ? '✅' : assignment.status === 'passive' ? '⏸️' : '🔄'}
                          </AssignmentText>
                          <AssignmentTagButtons>
                            {isBuraxilis && assignment.questionNumbers && assignment.questionNumbers.length > 0 && (
                              <EditButton
                                onClick={() => {
                                  setEditAssignment(assignment);
                                  setQuestionsToKeep(assignment.questionNumbers ? [...assignment.questionNumbers] : []);
                                  setEditPrefixFilter(assignment.studentIdPrefixFilter);
                                  // Load available prefixes for this assignment
                                  const selectedExam = getSelectedExam();
                                  if (selectedExam && assignment.examType === 'buraxilis') {
                                    setEditPrefixLoading(true);
                                    getStudentPrefixesForAssignment(
                                      selectedExam.collection,
                                      assignment.sinif,
                                      assignment.variant || assignment.qrup,
                                      assignment.buildingName,
                                      assignment.fenn
                                    )
                                      .then(setEditAvailablePrefixes)
                                      .catch(() => setEditAvailablePrefixes([]))
                                      .finally(() => setEditPrefixLoading(false));
                                  } else {
                                    setEditAvailablePrefixes([]);
                                  }
                                }}
                                disabled={isEditingAssignment}
                              >
                                ✏️ Redaktə
                              </EditButton>
                            )}
                            <DeleteButton
                              onClick={() => setDeleteConfirm({ assignmentId: assignment.id, assignmentInfo })}
                              disabled={isDeleting}
                            >
                              🗑️ Sil
                            </DeleteButton>
                          </AssignmentTagButtons>
                        </AssignmentTag>
                      );
                    })}
                  </TeacherAssignments>
                );
              })()}

              {/* Atama Formu */}
              <AssignmentSection>
                <AssignmentTitle>Yeni Təyinat:</AssignmentTitle>
                <AssignmentForm>
                  {isBuraxilis ? (
                    <>
                      <BuraxilisAssignmentForm
                        sinif={assignmentForms[teacher.id]?.sinif ?? ''}
                        variant={assignmentForms[teacher.id]?.qrup ?? ''}
                        selectedFenn={assignmentForms[teacher.id]?.selectedFenn ?? ''}
                        fennOptions={teacher.fenn.split(',').map(f => normalizeFenn(f))}
                        onSinifChange={(v) => handleAssignmentFormChange(teacher.id, 'sinif', v)}
                        onVariantChange={(v) => handleAssignmentFormChange(teacher.id, 'qrup', v)}
                        onFennChange={(v) => handleAssignmentFormChange(teacher.id, 'selectedFenn', v)}
                        onFetchPrefixes={async (sinif, variant, fenn) => {
                          const selectedExam = getSelectedExam();
                          if (!selectedExam) return [];
                          return getStudentPrefixesForAssignment(
                            selectedExam.collection,
                            sinif,
                            variant,
                            undefined,
                            fenn
                          );
                        }}
                        onSubmit={async (payload) => {
                          const selectedExam = getSelectedExam();
                          if (!selectedExam) {
                            alert('Lütfən əvvəlcə imtahan seçin');
                            return;
                          }
                          const form = assignmentForms[teacher.id];
                          if (!form?.sinif || !form?.qrup || !form?.selectedFenn) {
                            alert('Lütfən sinif, variant və fənn seçin');
                            return;
                          }
                          setSubmittingTeacherId(teacher.id);
                          try {
                            const canonicalFenn = toCanonicalFenn(form.selectedFenn);
                            const result = await createAssignment(
                              teacher.id,
                              teacher.name,
                              canonicalFenn,
                              form.sinif,
                              form.qrup || '-',
                              selectedExam.collection,
                              'buraxilis',
                              form.buildingName,
                              payload.questionNumbers,
                              payload.studentIdPrefixFilter
                            );
                            if (result.success) {
                              alert(result.message);
                              await loadTeachers();
                            } else {
                              alert(result.message);
                            }
                          } catch (err) {
                            console.error('Təyinat yaradılarkən xəta:', err);
                            alert('Təyinat yaradılarkən xəta baş verdi');
                          } finally {
                            setSubmittingTeacherId(null);
                          }
                        }}
                        disabled={teacher.isActive === false}
                        loading={submittingTeacherId === teacher.id}
                      />
                    </>
                  ) : (
                    <>
                      {(() => {
                        const fennList = teacher.fenn.split(',').map(f => normalizeFenn(f));
                        return fennList.length > 1 && (
                          <AssignmentRow style={{ marginBottom: '8px' }}>
                            <SmallSelect
                              value={assignmentForms[teacher.id]?.selectedFenn || ''}
                              onChange={(e) => handleAssignmentFormChange(teacher.id, 'selectedFenn', e.target.value)}
                              style={{ gridColumn: '1 / -1', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderColor: '#f59e0b', fontWeight: '600' }}
                            >
                              <option value="">Fənn seçin</option>
                              {fennList.map(fenn => (
                                <option key={fenn} value={fenn}>{fenn}</option>
                              ))}
                            </SmallSelect>
                          </AssignmentRow>
                        );
                      })()}
                      <AssignmentRow>
                        <SmallSelect
                          value={assignmentForms[teacher.id]?.sinif || ''}
                          onChange={(e) => handleAssignmentFormChange(teacher.id, 'sinif', e.target.value)}
                        >
                          <option value="">Sinif Seç</option>
                          {SINIFLAR.map(sinif => (
                            <option key={sinif} value={sinif}>{sinif}</option>
                          ))}
                        </SmallSelect>
                        <SmallSelect
                          value={assignmentForms[teacher.id]?.qrup || ''}
                          onChange={(e) => handleAssignmentFormChange(teacher.id, 'qrup', e.target.value)}
                        >
                          <option value="">Qrup Seç</option>
                          {QRUPLAR.map(qrup => (
                            <option key={qrup} value={qrup}>{qrup}</option>
                          ))}
                        </SmallSelect>
                        <SmallButton 
                          onClick={() => handleCreateAssignment(teacher)}
                          disabled={teacher.isActive === false}
                        >
                          ➕ Təyin Et
                        </SmallButton>
                      </AssignmentRow>
                    </>
                  )}
                  {/* Pasif Et / Aktif Et */}
                  {(
                    (teacher.isActive !== false && 
                     teacherAssignments[teacher.id] && 
                     teacherAssignments[teacher.id].some(a => a.status === 'active')
                    ) ||
                    (teacher.isActive === false)
                  ) && (
                    <SmallButton 
                      variant="danger"
                      onClick={() => handleToggleTeacherStatus(teacher.id, teacher.isActive !== false)}
                    >
                      {teacher.isActive !== false ? '⏸️ Pasif Et' : '▶️ Aktif Et'}
                    </SmallButton>
                  )}
                </AssignmentForm>
              </AssignmentSection>
            </TeacherCard>
          ))}
        </TeachersList>
      )}

      {/* Edit Assignment (Buraxılış – sual çıxar) Modal */}
      <EditModal show={!!editAssignment} onClick={() => !isEditingAssignment && setEditAssignment(null)}>
        <EditModalContent onClick={(e) => e.stopPropagation()}>
          {editAssignment && (
            <>
              <ConfirmDialogTitle>✏️ Atamadan sualları redaktə et</ConfirmDialogTitle>
              <ConfirmDialogText style={{ marginBottom: '16px' }}>
                <strong style={{ color: '#1e293b' }}>
                  {editAssignment.fenn} • Sinif {editAssignment.sinif} • Variant {editAssignment.variant || editAssignment.qrup}
                </strong>
              </ConfirmDialogText>
              <ConfirmDialogText style={{ marginBottom: '16px', fontSize: '14px' }}>
                Seçili qalan suallar bu hocada qalacaq; seçimi ləğv etdiyiniz suallar atamadan çıxarılacaq (serbest buraxılacaq).
              </ConfirmDialogText>
              <BuraxilisQuestionCheckboxGroup
                availableQuestions={editAssignment.questionNumbers ?? []}
                selectedQuestionNumbers={questionsToKeep}
                onChange={setQuestionsToKeep}
                disabled={isEditingAssignment}
                label="Bu təyinatda qalan suallar (seçimi ləğv etdiyiniz suallar atamadan çıxarılacaq)"
              />
              <div style={{ marginTop: '16px' }}>
                <PrefixFilterPanel
                  availablePrefixes={editAvailablePrefixes}
                  value={editPrefixFilter}
                  onChange={setEditPrefixFilter}
                  loading={editPrefixLoading}
                  disabled={isEditingAssignment}
                  label="Prefix filtr (iş nömrəsi)"
                />
              </div>
              <ConfirmDialogButtons style={{ marginTop: '24px' }}>
                <ConfirmButton
                  variant="cancel"
                  onClick={() => setEditAssignment(null)}
                  disabled={isEditingAssignment}
                >
                  Ləğv et
                </ConfirmButton>
                <ConfirmButton
                  variant="danger"
                  onClick={async () => {
                    if (!editAssignment) return;
                    if (questionsToKeep.length === 0) {
                      alert('Ən azı bir sual qalmalıdır');
                      return;
                    }
                    setIsEditingAssignment(true);
                    try {
                      const [questionsResult, prefixResult] = await Promise.all([
                        updateBuraxilisAssignmentQuestions(editAssignment.id, questionsToKeep),
                        updateAssignmentPrefixFilter(editAssignment.id, editPrefixFilter ?? null)
                      ]);
                      if (questionsResult.success && prefixResult.success) {
                        alert(questionsResult.message);
                        setEditAssignment(null);
                        await loadTeachers();
                      } else {
                        const failMsg = !questionsResult.success ? questionsResult.message : prefixResult.message;
                        alert(failMsg);
                      }
                    } catch (err) {
                      console.error('Təyinat yenilənərkən xəta:', err);
                      alert('Təyinat yenilənərkən xəta baş verdi');
                    } finally {
                      setIsEditingAssignment(false);
                    }
                  }}
                  disabled={isEditingAssignment}
                  style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: 'white' }}
                >
                  {isEditingAssignment ? 'Yenilənir...' : 'Təsdiqlə'}
                </ConfirmButton>
              </ConfirmDialogButtons>
            </>
          )}
        </EditModalContent>
      </EditModal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog show={!!deleteConfirm} onClick={() => !isDeleting && setDeleteConfirm(null)}>
        <ConfirmDialogContent onClick={(e) => e.stopPropagation()}>
          <ConfirmDialogTitle>⚠️ Atama Silmə Təsdiqi</ConfirmDialogTitle>
          <ConfirmDialogText>
            Bu atamanı silmək istədiyinizə əminsiniz?
            <br /><br />
            <strong style={{ color: '#1e293b' }}>
              {deleteConfirm?.assignmentInfo}
            </strong>
            <br /><br />
            Bu əməliyyat geri alına bilməz. Atama <strong>yoxla-s</strong> koleksiyonundan tamamilə silinəcək.
          </ConfirmDialogText>
          <ConfirmDialogButtons>
            <ConfirmButton 
              variant="cancel" 
              onClick={() => setDeleteConfirm(null)}
              disabled={isDeleting}
            >
              Ləğv et
            </ConfirmButton>
            <ConfirmButton 
              variant="danger" 
              onClick={handleDeleteAssignment}
              disabled={isDeleting}
            >
              {isDeleting ? 'Silinir...' : 'Bəli, Sil'}
            </ConfirmButton>
          </ConfirmDialogButtons>
        </ConfirmDialogContent>
      </ConfirmDialog>
    </Container>
  );
};

export default TeacherManagement;


